import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactGA from 'react-ga4';

import ShieldIcon from '@mui/icons-material/Shield';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Box, Container, Button, Typography, useMediaQuery, useTheme, Dialog, DialogContent, DialogTitle, DialogActions, Chip, IconButton, Slide, Snackbar, Tooltip } from '@mui/material';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ComputerIcon from '@mui/icons-material/Computer';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import FavoriteIcon from '@mui/icons-material/Favorite';

import { useNavigate } from 'react-router-dom';
import { setPersonModalOpen } from '../../../redux/slices/shared/sharedSlice';

import { LazyLoadImage } from 'react-lazy-load-image-component';
import BlankProfilePicture from '../../../assets/blank_profile_picture.png';
import '../../../css/PfpStyles.css'
import 'react-lazy-load-image-component/src/effects/blur.css';

const BulletPoint = ({size, weight, marginLeftRight, marginTopBottom, onMobile}) => {
    const multiplier = !onMobile ? 1 : 1.35;
    return (
        <Typography
            className="spacer"
            sx={{
                fontSize: `${size}rem`,
                fontWeight: weight,
                margin: `${marginTopBottom * multiplier}rem ${marginLeftRight * multiplier}rem`,
            }}
        >
            &nbsp;
            &#x2022;
            &nbsp;
        </Typography>
    )
}

const PersonCard = () => {
    const onMobile = !useMediaQuery('(min-width:600px)');
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const showMoreRef = useRef(null);

    const { personInfo, location } = useSelector(state => state.physician);

    const [message, setMessage] = useState("")
    const [insuranceDialog, setInsuranceDialog] = useState(false);
    const [phoneClicked, setPhoneClicked] = useState(false);
    const [transition, setTransition] = useState(undefined);
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [distance, setDistance] = useState(null);
    const [drivingTime, setDrivingTime] = useState(null);
    const [drivingUrl, setDrivingUrl] = useState(null);
    const [publicTransitTime, setPublicTransitTime] = useState(null);
    const [publicTransitUrl, setPublicTransitUrl] = useState(null);
    const [bicyclingTime, setBicyclingTime] = useState(null);
    const [bicyclingUrl, setBicyclingUrl] = useState(null);
    const [showMoreBio, setShowMoreBio] = useState(false);
    const [copyToClipboardText, setCopyToClipboardText] = useState("Copy to Clipboard");
    const [bioOverflowing, setBioOverflowing] = useState(false);

    const modalStyles = onMobile ?{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        height: "",
        flexDirection: 'column',
        justifyContent: '',
        alignItems: 'center',
        width: "100vw",
        minHeight: "90%",
        maxWidth: "100%",
        maxHeight: "85vh",
        minWidth: "95%",
        bgcolor: 'white',
        borderRadius: '0rem',
        boxShadow: 24,
        px: 2,
        pb: 3,
        overflowY: !onMobile ? "" : "scroll",
    } : {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: '',
        minHeight: "30rem",
        maxHeight: '50rem',
        minWidth: '50rem',
        maxWidth: '60rem',
        bgcolor: 'white',
        borderRadius: !onMobile ? '1rem' : '0rem',
        boxShadow: 24,
        pt: 2,
        px: 3,
        pb: 3,
        overflowY: !onMobile ? "" : "scroll",

    };

    const textStyleOne = !onMobile ? {
        flexDirection: 'row',
        fontWeight: 800,
        fontSize: '1.75em',
        lineHeight: '1em',
        textAlign: 'left',
        marginTop: '0.5rem',
        marginBottom: '0.5rem'
    }: {
        margin: "0.75rem 0",
        fontWeight: 800,
        fontSize: '1.5em',
        lineHeight: '1.1em',
    }

    const textStyleTwo = {
        fontSize: "1rem",
        maxHeight: showMoreBio ? "" : "8rem",
        minHeight: showMoreBio ? "87%" : "60%",
        overflow: showMoreBio ? "" : "hidden",
        textOverflow: "ellipsis"
    }

    const textStyleThree = !onMobile ? {
        display: "flex",
        flexDirection: "row",
        fontSize: '1.1em',
    } : {
        display: "flex",
        flexDirection: "row",
        fontWeight: 500,
        fontSize: '1em',
        lineHeight: '1.1em',
    }

    const textStyleFour = !onMobile ? {
        flexDirection: 'row',
        fontWeight: 400,
        fontSize: '1.6em',
        fontHeight: '1em',
        textAlign: 'left',
        marginTop: '0.5rem',
        marginBottom: '0.5rem'
    } : {
        marginBottom: "0.5rem",
        fontWeight: 600,
        fontSize: '1.3em',
        lineHeight: '1.1em',
    }

    const reasonsForMatchingText = {
        marginLeft: !onMobile ? '0.75rem' : '0.5rem',
        marginTop: !onMobile ? "0.2rem" : "0.1rem",
        fontSize: !onMobile ? "1.1em" : "1rem",
        lineHeight: !onMobile ? "" : "1.2rem",
    }

    const chipStyles = {
        margin: "0.1rem 0.4rem", 
        backgroundColor: "rgba(0,0,0,0.75)", 
        color: "white",
        "&:hover": {
            backgroundColor: "rgba(0,0,0,0.6)",
            color: "white",
        }
    }

    useEffect(() => {
        getDistance();
        //If the bio has loaded, check if it is overflowing
        if(showMoreRef.current)
            setBioOverflowing(showMoreRef.current.clientHeight < showMoreRef.current.scrollHeight);

    }, [personInfo]);

    const getPfpProps = () => {
        if (personInfo && personInfo.Files && personInfo.Files.length && personInfo.Files[0].url)
            return {
                style: { width: '100%' },
                src: personInfo.Files[0].url,
                alt: "doctor",
                effect: 'blur'
            }
        if(personInfo?.avatar)
            return {
                style: { width: '100%' },
                src: personInfo.avatar,
                alt: "doctor",
                effect: 'blur'
            }
        return {
            style: { width: '100%' },
            src: BlankProfilePicture,
            alt: "blank profile",
            effect: 'blur'
        }
    }

    //close snackbar
    const handleSnackBarClose = () => {
        setSnackBarOpen(false);
    };

    //close insurance dialog
    const handleOpenInsuranceDialog = (e) => {
        e.preventDefault();
        setInsuranceDialog(true);
    }

    //send GA event when phone number is clicked and show phone number
    const handlePhoneNumberClicked = (e) => {
        e.preventDefault();
        ReactGA.event({
            category: 'Button',
            action: 'Click',
            label: 'Phone Number'
        });
        setPhoneClicked(true);
    }

    //close insurance dialog
    const handleCloseInsuranceDialog = (e) => {
        e.preventDefault();
        setInsuranceDialog(false);
    }

    //send GA event that user attempted to make an appointment
    const handleBookAppointment = () => {
        ReactGA.event({
            category: 'Button',
            action: 'Click',
            label: 'Request Appointment'
        });
        window.open(personInfo.site);
    }

    //if there are strongestTraitMatches, return the reasons for matching
    const reasonsForMatching = () => {
        return (
            <Box aria-label="match-reasons">
                {personInfo.strongestTraitMatches ?
                    getReasonsForMatching()
                    : null
                }
            </Box>
        )
    }

    /**
     * Close the modal
     * @param {Event} e 
     */
    const handleCloseModal = async (e) => {
        e.preventDefault();
        dispatch(setPersonModalOpen(false));
    }

    /***
     * Navigate to questionnaire
     */
    const goToQuestionnaire = () => {
        navigate("/onboard/questionnaire/0");
    }

    /***
     * Create transition up for snackbar
     * 
     * @param {object} props
     * @returns {object} Slide
     */
    const TransitionUp = (props) => {
        return <Slide {...props} direction="up" />;
    }

    /***
     * Open snackbar
     * 
     * @param {object} Transition
     */
    const handleSnackBarOpen = (Transition) => {
        setTransition(() => Transition);
        setSnackBarOpen(true);
    };

    /***
     * Format phone number from db
     * 
     * @param {string} phoneNumber
     * @returns {string} formatted phone number
     */
    const formatPhoneNumber = (phoneNumber) => {
        const cleaned = ('' + phoneNumber).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
          return '(' + match[1] + ')-' + match[2] + '-' + match[3];
        }
        return phoneNumber;
    }

    /***
     * Toggle "show more" for bio
     * 
     * @param {event} e
     */
    const onClickToggleBio = (e) => {
        e.preventDefault();
        setShowMoreBio(!showMoreBio);
    }

    /***
     * Copy the current doctor's location to the clipboard
     * 
     * @param {event} e
     */
    const copyToClipboard = (e) => {
        e.preventDefault();
        navigator.clipboard.writeText(location[0].address);
        if(!onMobile) {
            setCopyToClipboardText("Copied to clipboard");
            setTimeout(() => {
                setCopyToClipboardText("Copy to clipboard");
            }, 2000);
        } else {
            setMessage("Copied to clipboard!");
            handleSnackBarOpen(TransitionUp);
        }
    }

    /***
     * Get the distance between the user and the doctor
     */
    const getDistance = async () => {
        // const distancesAndTimes = dispatch(findDoctorDistance());
        const distancesAndTimes = {
            distance: "1.2 mi",
            drivingTime: "5 mins",
            publicTransitTime: "10 mins",
            bicyclingTime: "15 mins"
        }
        if(distancesAndTimes)
            setTravelDistancesAndTimes(distancesAndTimes);
    }

    /***
     * Set the distance and time between the user and the doctor
     * 
     * @param {object} distancesAndTimes - the distance and time between the user and the doctor
     * @param {object} docLatLong - the latitude and longitude of the doctor
     */
    const setTravelDistancesAndTimes = (distancesAndTimes) => {
        const { distance, drivingTime, publicTransitTime, bicyclingTime } = distancesAndTimes;
        setDistance(distance);

        setDrivingTime(drivingTime);
        // setDrivingUrl(`https://www.google.com/maps/dir/?api=1&origin=${latLong.lat},${latLong.lng}&destination=${docLatLong.lat},${docLatLong.lng}&travelmode=driving`);
        
        setPublicTransitTime(publicTransitTime);
        // setPublicTransitUrl(`https://www.google.com/maps/dir/?api=1&origin=${latLong.lat},${latLong.lng}&destination=${docLatLong.lat},${docLatLong.lng}&travelmode=transit`);
        
        setBicyclingTime(bicyclingTime);
        // setBicyclingUrl(`https://www.google.com/maps/dir/?api=1&origin=${latLong.lat},${latLong.lng}&destination=${docLatLong.lat},${docLatLong.lng}&travelmode=bicycling`);
    }

    /***
     * Open the doctor's location in Google Maps
     * 
     * @param {event} e
     * @param {string} mode - the mode of transportation
     * @example mode = "car"
     */
    const openMapsUrl = (e, mode) => {
        e.preventDefault();
        switch(mode) {
            case "car":
                window.open(drivingUrl);
                break;
            case "bus":
                window.open(publicTransitUrl);
                break;
            case "bicycle":
                window.open(bicyclingUrl);
                break;
            default:
                break;
        }
    }
    
    /***
     * Add the doctor to the user's favorites when the user clicks the favorite icon
     * 
     * @returns {component} - the favorite icon filled in if the doctor is a favorite, otherwise the favorite icon outlined
     */
    const heartIcon = () => {
        return (
            <Tooltip title="Remove Favorite">
                <FavoriteIcon sx={{ color: theme.palette.caralystRed.main }}/>
            </Tooltip>
        );
    }

    /***
     * Get the reasons for matching between the user and the doctor
     * 
     * @returns {component} - the reasons for matching between the user and the doctor
     */
    const getReasonsForMatching = () => {
        const reasonsToMatch = personInfo.strongestTraitMatches.map((trait) => trait.reasons_for_matching);
        
        return reasonsToMatch.length > 0 ?
            <Box>
                <Typography 
                    className="person-matching-header" 
                    color="black"
                    sx={ textStyleOne }
                >
                    {reasonsToMatch.length === 1 ?
                        "You matched on 1 dimension"
                        :
                        `You matched on ${reasonsToMatch.length} dimensions`
                    }
                </Typography>
                <Box className="person-matching-container" >
                    {reasonsToMatch.map((reason, index) => {
                        if (reason) return (
                            <Container 
                                className="match-reason-container" 
                                key={`${index}-matchreason`}
                                sx={{
                                    display: 'flex', 
                                    flexDirection: 'row', 
                                    alignItems: 'center', 
                                    marginLeft: !onMobile ? "-1.25rem" : "-0.5rem", 
                                    marginBottom: "0.5rem",
                                }}
                                >
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    maxWidth: "1.25em",
                                    maxHeight: "1.25em",
                                    boxShadow: "0 3px 5px rgba(0, 0, 0, 0.4)", // Adjust the values as per your preference
                                    borderRadius: "50%",
                                    backgroundColor: "transparent",
                                }}>
                                    <i className="fas fa-check-circle fa-2x" style={{
                                        color: theme.palette.caralystGreen.main,
                                        fontSize: !onMobile ? "1.6em" : "1.5em",

                                    }}/>        
                                </Box>
                                <Box sx={{
                                    marginLeft: "0.5rem",
                                }}>
                                    <Typography 
                                        className="match-reason"
                                        sx={reasonsForMatchingText}
                                    >
                                        {reason} 
                                    </Typography>
                                </Box>
                            </Container>
                        )
                        return null;
                    })}
                </Box>
            </Box>
            :
            //Give something so they know why nothing is showing up
            //Style better later
            null
    }

    /***
     * Get the doctor's bio and basic information
     * 
     * @returns {component} - the doctor's bio and basic information
     */
    const docBio = () => {
        return (
            <Box aria-label="bio"
                sx={{
                    marginTop: "1rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    maxWidth: "90%",
                    marginRight: "2.5rem",
                    overflowY: "scroll",
                    maxHeight: "20rem",
                }}
            >
                    <Box sx={{
                        marginBottom: "1rem",
                    }}>
                        <Typography
                            sx={ textStyleOne}
                        >
                            {personInfo.first_name}'s Story:
                        </Typography>
                            {personInfo.bio !== null?
                                <Box>
                                    <Box sx={{position: "relative"}}>
                                        <Typography 
                                            ref={showMoreRef}
                                            sx={ textStyleTwo }
                                        >
                                            {personInfo.bio}
                                        </Typography>
                                        {bioOverflowing && !showMoreBio &&
                                            <Box aria-label="new-box"
                                                sx={{
                                                    position: "absolute",
                                                    bottom: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "2.5rem",
                                                    zIndex: 1,
                                                    background: `linear-gradient(to bottom, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 1)) `,
                                                    overlap: "auto",
                                                }}
                                            />
                                        }
                                    </Box>
                                    {bioOverflowing && 
                                        <Typography
                                            sx={{
                                                position: "relative",
                                                cursor: "pointer",
                                                fontSize: "1rem",
                                                fontStyle: 'normal',
                                                color: theme.palette.caralystRed.main,
                                            }}
                                            onClick={onClickToggleBio}
                                        >
                                            {showMoreBio ? "Hide" : "Read More"}
                                        </Typography>
                                    }
                                    </Box>
                                    :
                                    "Add your bio"
                            }
                    </Box>
                {personInfo.education !== null ?
                    <Box
                        sx={{
                            marginBottom: "1rem",
                        }}
                    >
                        <Typography
                            sx={ textStyleOne }
                        >
                            Education
                        </Typography>
                        <Typography sx={ textStyleThree }>
                            {personInfo.education}
                        </Typography>
                    </Box>
                    : null
                }
                {personInfo.residency !== null ?
                    <Box
                        sx={{
                            marginBottom: "1rem",
                        }}
                    >
                        <Typography
                            sx={ textStyleOne }
                        >
                            Residency
                        </Typography>
                        <Typography sx={ textStyleThree }>
                            {personInfo.residency}
                        </Typography>
                    </Box>
                    : null
                }
            </Box>
        );
    }

    /***
     * Get the doctor's information
     * 
     * @returns {component} - the doctor's information
     */
    const docInfoWrapper = () => {
        return (
            <Box className="doc-info-wrapper"
                sx={!onMobile ? {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    minWidth: "15rem",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    overflowY: "auto",
                } : {
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box sx={ !onMobile ? {maxWidth: "95%"} : {maxWidth: "97%", overflowWrap: "break-word", maxHeight: "100%", overflowY: "auto"}}>
                    <Typography
                        color={theme.palette.caralystNavy.main}
                        sx={textStyleOne}
                    >
                        {personInfo.first_name ? 
                            personInfo.first_name
                            :
                            "First Name"
                        }
                        {" "}
                        {personInfo.last_name ?
                            personInfo.last_name
                            :
                            "Last Name"

                        }
                        {!personInfo.user_qualification ? 
                            ", Qualification" 
                            : 
                            ", " + personInfo.user_qualification
                        }    
                    </Typography>
                    <Typography sx={ textStyleFour }>
                        {personInfo?.user_specialty ?
                                personInfo.user_specialty
                                :
                                "Add your specialty"
                        }
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            justifyContent: "flex-start",
                        }}
                    >
                        {
                            <Box 
                                className="location" 
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    margin: "0.2rem 0",
                                }}
                            >
                                <Box aria-label="distance-and-location" sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                }}>
                                    <Tooltip title="If you have multiple locations, the one displayed will be whichever the patient has selected on the map.">
                                        <LocationOnIcon 
                                            sx={{ 
                                                fontSize: !onMobile ? '1.5em' : '1.35em',
                                                color: "black"
                                            }}
                                        />
                                    </Tooltip>
                                    
                                    &nbsp;
                                    {
                                        location?.length > 0 ?
                                            <>
                                                <Typography aria-label="distance"
                                                    sx={{...textStyleThree, whiteSpace: "nowrap"}}
                                                > 
                                                    {distance &&
                                                        <>
                                                            {distance} 
                                                            <BulletPoint size={1.95} weigth={800} marginLeftRight={-0.6} marginTopBottom={-0.6} onMobile={onMobile} />
                                                        </>
                                                        
                                                    }
                                                </Typography>
                                                <Typography aria-label="location"
                                                    sx={ textStyleThree }
                                                >
                                                    {location[0].address.split(",")[0]}
                                                    &nbsp;
                                                    <Tooltip 
                                                        title={copyToClipboardText}
                                                        placement="right"
                                                    >
                                                        <ContentCopyIcon 
                                                            onClick={copyToClipboard}
                                                            sx={{ 
                                                                fontSize: '0.8em',
                                                                marginTop: "0.3rem",
                                                                cursor: 'pointer',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </Typography>
                                            </>
                                            :
                                            <Typography aria-label="location"
                                                sx={ textStyleThree }
                                            >
                                                Add your location
                                                &nbsp;
                                                <Tooltip 
                                                    title={copyToClipboardText}
                                                    placement="right"
                                                >
                                                    <ContentCopyIcon 
                                                        onClick={copyToClipboard}
                                                        sx={{ 
                                                            fontSize: '0.8em',
                                                            marginTop: "0.3rem",
                                                            cursor: 'pointer',
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Typography>
                                    }
                                    
                                </Box>
                                {(drivingTime || publicTransitTime || bicyclingTime) &&
                                    <Box aria-label="travel-options">
                                        {drivingTime && 
                                            <Tooltip title="Directions by Car">
                                                <Chip 
                                                    size="small" 
                                                    avatar={
                                                        <DirectionsCarIcon 
                                                            style={{color: "white", cursor: 'pointer'}}
                                                        />
                                                    } 
                                                    label={drivingTime} 
                                                    sx={chipStyles}
                                                    onClick={e => openMapsUrl(e, "car")}
                                                />
                                            </Tooltip>
                                        }
                                        {publicTransitTime && 
                                            <Tooltip title="Directions by Bus">
                                                <Chip 
                                                    size="small" 
                                                    avatar={
                                                        <DirectionsBusIcon 
                                                            style={{color: "white"}}
                                                        />
                                                    } 
                                                    label={publicTransitTime} 
                                                    sx={chipStyles}
                                                    onClick={e => openMapsUrl(e, "bus")}
                                                />
                                            </Tooltip>
                                        }
                                        {bicyclingTime && 
                                            <Tooltip title="Directions by Bicycle">
                                                <Chip 
                                                    size="small" 
                                                    avatar={
                                                        <DirectionsBikeIcon 
                                                            style={{color: "white"}}
                                                        />
                                                    } 
                                                    label={bicyclingTime} 
                                                    sx={chipStyles}
                                                    onClick={e => openMapsUrl(e, "bicycle")}
                                                />
                                            </Tooltip>
                                        }
                                    </Box>
                                }
                                
                            </Box>
                        }

                        <Box className="insurance" sx={{margin: "0.4rem 0"}}>
                            {//If the patient has only selected "Any Insurance"
                                personInfo.Insurances && personInfo.Insurances.length === 0 && 
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "flex-start",
                                            cursor: 'pointer'
                                        }}
                                        onClick={handleOpenInsuranceDialog}
                                    >
                                        <ShieldIcon 
                                            sx={{ 
                                                fontSize: !onMobile ? '1.5em' : '1.35em',
                                                color: theme.palette.burntOrange.main,
                                            }}
                                        /> 
                                        &nbsp;
                                        <Typography     
                                            className='accepted-insurance'
                                            sx={{...textStyleThree}}
                                        >
                                            See if they're in your network
                                        </Typography> 
                                    </Box>
                            }  
                            {personInfo.Insurances && personInfo.Insurances.length > 0 &&
                                <Box sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                }}>
                                    <ShieldIcon 
                                        sx={{ 
                                            fontSize: !onMobile ? '1.5em' : '1.35em',
                                            color: theme.palette.limeGreen.main 
                                        }}
                                    /> 
                                    &nbsp; 
                                    <Box sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        flexWrap: "wrap",
                                    }}>
                                        <Typography 
                                            aria-label='accepted-insurance'
                                            sx={{...textStyleThree, whiteSpace: !onMobile ? "" : "nowrap"}}
                                        >
                                            In network 
                                            <BulletPoint size={1.95} weigth={800} marginLeftRight={-0.6} marginTopBottom={-0.6} onMobile={onMobile} />
                                            {personInfo?.Insurances[0].name} 
                                        </Typography> 
                                    </Box>
                                </Box>
                            }
                            </Box>
                            <Box 
                                onClick={handlePhoneNumberClicked}
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    margin: "0.2rem 0",
                                }}
                            >
                                <LocalPhoneIcon 
                                    sx={{
                                        color: "black",
                                        fontSize: !onMobile ? '1.5em' : '1.35em',
                                    }}
                                />
                                &nbsp;
                                <Typography sx={ textStyleThree }
                                >
                                    {personInfo?.phone ? 
                                        formatPhoneNumber(personInfo.phone)
                                        :
                                        "Add your phone number"
                                    }
                                </Typography>
                            </Box>
                            <Box 
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    margin: "0.25rem 0",
                                }}
                            >
                                <ComputerIcon 
                                    sx={{
                                        color: "black",
                                        fontSize: !onMobile ? '1.5em' : '1.35em',
                                    }}
                                />
                                &nbsp;
                                <Typography sx={ textStyleThree }>
                                    {personInfo?.modality ?
                                        personInfo.modality
                                        :
                                        "Add your modality"
                                    }
                                </Typography>
                            </Box>
                    </Box>
                </Box>
                <Box className="spacer" sx={{height: "0.5rem"}}/>
                <Box>
                    <Tooltip title={`The current site being used is ${personInfo.site}`}>
                        <Button
                            className="appointment-button"
                            variant="contained"
                            disabled={!personInfo.site}
                            sx={personInfo.site === null ? { backgroundColor: '#fff', minHeight: '3rem' } : { 
                                backgroundColor: theme.palette.caralystRed.main,
                                minHeight: '3rem',
                                "&:hover": {
                                    backgroundColor: theme.palette.caralystRed.main,
                                    brightness: 0.8,
                                },
                                boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.25)",
                                marginBottom: "1rem",
                                marginTop: "0.5rem"
                            }}
                            onClick={() => { handleBookAppointment() }}>
                            <CalendarMonthIcon 
                                className="request-appointment-icon" 
                                sx={{
                                    color: "white",
                                    marginRight:"0.5rem",
                                    maxWidth:"4rem"
                                }}
                            />
                            <Typography
                                fontSize="1rem"
                                lineHeight={1.1}
                                textTransform="none"
                                color="white"
                                textAlign="left"
                            >
                                {personInfo.site ?
                                    "Request Appointment"
                                    :
                                    "Add your appointment URL"
                                }
                            </Typography>
                        </Button>
                    </Tooltip>
                </Box>
                <Box aria-label="match-and-reviews"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            minWidth: "55%",
                            marginTop: "0.5rem",
                        }}
                    >
                            <Typography
                                component={"div"}
                                sx={{
                                    fontSize: '1.1em',
                                }}
                            >
                                Take our three minute compatibility survey &nbsp; 
                                <Box
                                    sx={{
                                        display: "inline",
                                        textDecoration: 'underline',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                    }}
                                    onClick={goToQuestionnaire}
                                >
                                    here
                                </Box> to see why {personInfo.first_name} is a perfect match for you!
                            </Typography>
                        <Box aria-label="reviews">

                        </Box>
                    </Box>
            </Box>
        );
    }

    return (
        <>
            <Box className="person-card-wrapper" sx={modalStyles}>
                <Box className="left-half"
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        marginTop: "3%",
                        width: !onMobile ? "50%" : "100%",
                    }}
                >
                    <Box className="pfp-wrapper"
                        sx={!onMobile ? {
                            width: "40vh",
                            maxWidth: "85%",
                            maxHeight: "60%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid #E0E0E0",
                            borderRadius: "0.5rem",
                            overflowY: "hidden",
                        } : {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "18rem",
                            marginLeft: "calc(50% - 9rem)",
                            marginBottom: "0.5rem",
                            marginTop: "1.25rem",
                        }}
                        key={personInfo.id}
                    >
                        <LazyLoadImage 
                            {...getPfpProps()}
                            className="pfp"
                        />
                    </Box>
                    {!onMobile && docBio()}
                    {onMobile && docInfoWrapper()}
                </Box>
                <Box aria-label="right-half"
                    sx={!onMobile ? {
                        display: "flex",
                        flexDirection: "column",
                        marginTop: "3%",
                        marginBottom: "2rem",
                        width: "50%",
                        overflowY: "auto",
                    } : {
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                    }}
                >
                    {!onMobile && docInfoWrapper()}
                    {!!onMobile && docBio()}
                </Box>
                <Box aria-label="footer"
                    sx={{
                        position: !onMobile ? "absolute" : "sticky",
                        bottom: !onMobile ? "0.5rem" : "-1rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        marginTop: !onMobile ? "" : "1rem",
                    }}
                >
                    <Button
                        variant='outlined'
                        sx={{
                            borderRadius: "2rem",
                            minWidth: "5rem",
                            transition: "background-color 0.15s ease-in-out",
                            marginRight: "0.5rem",
                            backgroundColor: 'white',
                            borderColor: theme.palette.caralystGreen.main,
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                                borderColor: theme.palette.caralystGreen.main,
                            }
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '1em',
                                fontWeight: '500',
                                fontStyle: 'normal',
                            }}
                        >
                            Back
                        </Typography>
                    </Button>
                    <Button
                        variant='outlined'
                        sx={{
                            borderRadius: "2rem",
                            minWidth: "5rem",
                            transition: "background-color 0.15s ease-in-out",
                            marginLeft: "0.5rem",
                            backgroundColor: 'white',
                            borderColor: theme.palette.caralystGreen.main,
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                                borderColor: theme.palette.caralystGreen.main,
                            }
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '1em',
                                fontWeight: '500',
                                fontStyle: 'normal',
                            }}
                        >
                            Next
                        </Typography>
                    </Button>
                    <IconButton
                        sx={{
                            position: "absolute",
                            right: onMobile ? "-1.5rem" : "0.5rem",
                        }}
                    >
                        {heartIcon()}
                    </IconButton>
                </Box>
                <Box aria-label="x-out-button">
                    <IconButton
                        onClick={async (e) => await handleCloseModal(e)}
                        sx={{
                            position: "absolute",
                            top: "0.5rem",
                            right: "0.5rem",
                            rotate: "90deg",
                    }}>
                        <CloseFullscreenIcon />
                    </IconButton>
                </Box>
            </Box>
            <Dialog
                open={insuranceDialog}
                onClose={handleCloseInsuranceDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="xl"
            >
                <DialogContent>
                    <DialogTitle id="alert-dialog-description">
                        Accepted Insurances:
                    </DialogTitle>
                    {personInfo?.Insurances?.map((insurance, index) => {
                        return (
                        <Chip 
                            label={insurance.name} 
                            variant="outlined" 
                            backgroundColor={theme.palette.caralystGreen.main}
                        />
                        )
                    })
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseInsuranceDialog} autoFocus>
                        Ok!
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                TransitionComponent={transition}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                open={snackBarOpen}
                onClose={handleSnackBarClose}
                message={message}
                key={'bottomright'}
                autoHideDuration={2000}
                sx={{ position: "inline-block", zIndex: 9999999}}
            />
        </>
        
    )
}

export default PersonCard;